import React, { useState, useCallback, FC, ChangeEvent } from 'react'
import { useTheme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';

import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Fab from '@mui/material/Fab'
import EditIcon from '@mui/icons-material/Edit'

import { getProps } from '@kaidam/fc-shared/src/frontend/components/form/fields/utils'

import {
  FormFieldProps,
} from '@kaidam/fc-shared/src/types/frontend/form'

import {
  IForm_Color,
} from '@kaidam/fc-shared/src/types/schema/form'

import { isRgb } from '@kaidam/fc-shared/src/frontend/utils/color'
import ColorPicker from '../../colorpicker/ColorPicker'

const useStyles = makeStyles(theme => ({
  margin: {
    margin: theme.spacing(1),
  },
  colorTab: {
    width: '50px',
    height: '50px',
    border: '1px solid #000',
    cursor: 'pointer',
    boxShadow: '10px 10px 20px 0px rgba(0,0,0,0.2)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogContent: {
    overflowX: 'hidden',
  },
  fab: {
    width: '20px',
    height: '20px',
    minHeight: '20px',
  },
  fabIcon: {
    width: '0.7em',
    height: '0.7em',
  }
}))

const ColorPickerField: FC<React.PropsWithChildren<FormFieldProps>> = (props) => {
  const {
    field: {
      name,
      value,
    },
    form: {
      setFieldValue,
    },
    item,
    error,
    touched,
    readonly,
  } = getProps(props)
  const classes = useStyles()
  const theme = useTheme()

  const [ open, setOpen ] = useState(false)
  const [ undoValue, setUndoValue ] = useState<IForm_Color | null>(null)

  const getCurrentValue = useCallback(() => {
    let inputValue: IForm_Color = value || {}

    if(typeof(inputValue) == 'string') inputValue = {color: inputValue}
    if(!inputValue.color) inputValue.color = theme.palette.primary.main

    const retValue = Object.assign({}, inputValue)
    if(!retValue.inputColor) retValue.inputColor = retValue.color
    return retValue
  }, [value, theme])

  const handleChangeTextField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const inputColorString = event.currentTarget.value

    const newValues: IForm_Color = {
      inputColor: inputColorString,
    }

    if(isRgb(inputColorString)) {
      newValues.color = inputColorString
    }

    const currentValue = getCurrentValue()
    const updateValues = Object.assign({}, currentValue, newValues)

    setFieldValue(name, updateValues)
  }, [name, getCurrentValue, setFieldValue])

  const handleChangeColorPicker = useCallback((value: IForm_Color) => {
    setFieldValue(name, value)
  }, [name, setFieldValue])

  const handleOpen = useCallback(() => {
    setOpen(true)
    setUndoValue(getCurrentValue())
  }, [getCurrentValue])

  const handleUndo = useCallback(() => {
    setFieldValue(name, undoValue)
  }, [name, undoValue])

  const handleClose = useCallback(() => {
    setOpen(false)
    setUndoValue(null)
  }, [name, undoValue])

  const handleCancel = useCallback(() => {
    handleUndo()
    handleClose()
  }, [handleUndo, handleClose])

  const useValue = getCurrentValue()
  const description = item.helperText

  return (
    <div>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            aria-describedby={ name + "-helper" }
            error={ touched && error ? true : false }
          >
            <InputLabel 
              htmlFor={ name }>{ item.title || item.id }</InputLabel>
            <Input
              id={ name }
              key={ name }
              disabled={ readonly }
              error={ touched && error ? true : false }
              value={ useValue.inputColor }
              onChange={ handleChangeTextField }
            />
            {
              touched && error ? (
                <FormHelperText id={ name + "-helper" }>
                  { error }
                </FormHelperText>
              ) : null
            }
            {
              description ? (
                <FormHelperText error={ false } id={ name + "-description" }>
                  { description }
                </FormHelperText>
              ) : null
            }
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          {
            !readonly && (
              <div 
                className={ classes.colorTab } 
                style={{
                  backgroundColor: value && value.color ? value.color : {color:'#ffffff'},
                }}
                onClick={ handleOpen }
              >
                <Fab 
                  className={ classes.fab }
                  size="small" 
                  aria-label="Add"
                >
                  <EditIcon className={ classes.fabIcon } />
                </Fab>
              </div>
            )
          }
        </Grid>
      </Grid>
      {
        open && (
          <Dialog
            open
            maxWidth="sm"
            fullWidth
            onClose={ handleClose }
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{ item.title || item.id }</DialogTitle>
            <DialogContent className={ classes.dialogContent }>
              <ColorPicker
                intent='primary'
                value={ useValue }
                onUpdate={ handleChangeColorPicker }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={ handleCancel } color="primary">
                Cancel
              </Button>
              <Button onClick={ handleClose } color="primary" autoFocus>
                OK
              </Button>
            </DialogActions>
          </Dialog>
        )
      }
    </div>
  )
}

export default ColorPickerField